// =====================================================================
// billingBrandConfig — CAPA DE MARCA (PROTECTED en el sync)
// ---------------------------------------------------------------------
// Esta es la versión de IMPULZA. Cada app (Impulza / Gastify) mantiene la
// suya. El núcleo de cobro (useSubscriptionStatus, astropayService,
// páginas de suscripción) la importa por inyección y NO debe hardcodear
// nada de marca, precios, pasarela ni credenciales.
//
// Regla del plan: en Impulza el módulo de cobro queda INACTIVO por
// defecto (flag por organización apagado) y en modo SANDBOX.
// =====================================================================

export const BILLING_BRAND = {
  // Identidad
  brandName: 'Gastify',
  supportContact: 'soporte de Gastify',
  // Canal de ayuda que ofrecen los recorridos guiados cuando el usuario no
  // puede resolver un paso solo. Vacio => el aviso se muestra sin enlace.
  supportHelpHref: 'https://wa.me/5493412739146?text=Hola!%20Necesito%20una%20mano%20con%20la%20puesta%20a%20punto%20de%20Gastify',
  supportHelpLabel: 'Escribinos por WhatsApp',

  // Flags por organización (deben existir en ALL_MODULES de organizationModules.js,
  // y NO estar en ningún PLAN_PRESET → apagados por defecto en todas las orgs).
  billingFlag: 'suscripcion-billing',
  onboardingFlag: 'onboarding-guiado',

  // Pasarela de cobro del abono
  paymentProvider: 'mercadopago', // 'mercadopago' | 'astropay'
  sandbox: false,                 // Producción. El token real vive como secret
  // MERCADOPAGO_ACCESS_TOKEN en el Supabase de Impulza.

  // URLs de retorno post-checkout (relativas al sistema)
  returnPath: '/sistema/suscripcion/estado',

  // Moneda mostrada por defecto (el precio real sale de subscription_pricing)
  currency: 'ARS',
};

const PROVIDER_META = {
  mercadopago: { provider: 'mercadopago', label: 'MercadoPago', short: 'MP' },
  astropay: { provider: 'astropay', label: 'AstroPay', short: 'AstroPay' },
};

export function getSubscriptionPaymentProviderMeta(provider) {
  return PROVIDER_META[provider] || PROVIDER_META[BILLING_BRAND.paymentProvider] || PROVIDER_META.mercadopago;
}

// =====================================================================
// Catálogo público de planes (marca). Estructura compartida con el
// núcleo (usePublicPlans), pero SIN precios reales: en Impulza son
// placeholders (price_monthly: 0). El precio real sale de la DB
// (subscription_pricing / subscription_plans) cuando el cobro se activa.
// =====================================================================
export const PUBLIC_PLAN_ORDER = ['basic', 'premium', 'enterprise'];

export const PUBLIC_PLAN_FALLBACKS = {
  basic: {
    marketingName: 'Plan Arranque',
    badge: 'Operación esencial',
    audience: 'Para locales chicos o negocios en su primera etapa de orden.',
    summary: 'Ventas, caja, clientes y stock en una sola rutina de trabajo.',
    price_monthly: 0,
    operation_limit: 500,
    modules: [
      'ventas-pos', 'ventas-historial', 'caja', 'clientes', 'facturacion', 'productos', 'inventario', 'inventario-ajuste',
      'inventario-transferencia', 'pedido-sugerido', 'gastos', 'rentabilidad-ventas', 'flujo-fondos', 'gestion-costos', 'listas-precios',
      'empresa-datos', 'permisos', 'sitios', 'categorias', 'unidades-medida', 'config-sistema-datos', 'importaciones',
      'metodos-cobro', 'condiciones-pago', 'tipos-comprobante', 'presupuestos-config', 'config-facturacion', 'config-reportes', 'logs'
    ],
    highlights: ['Facturación electrónica ARCA', 'Stock con alertas y pedido sugerido', 'Costos y listas de precios'],
    cta: 'Empezar con orden',
  },
  premium: {
    marketingName: 'Plan Control',
    badge: 'Crecimiento con método',
    audience: 'Para negocios con más movimiento, compras frecuentes y facturación.',
    summary: 'Compras, facturación y tesorería operativa sin depender de planillas.',
    price_monthly: 0,
    operation_limit: 2500,
    modules: [
      'ventas-pos', 'ventas-historial', 'caja', 'clientes', 'facturacion', 'productos', 'inventario', 'inventario-ajuste',
      'inventario-transferencia', 'compras-nueva', 'pedido-sugerido', 'compras', 'proveedores', 'bancos', 'tesoreria-cobranzas',
      'tesoreria-pagos', 'gastos', 'rentabilidad-ventas', 'flujo-fondos', 'gestion-costos', 'listas-precios',
      'empresa-datos', 'permisos', 'sitios', 'categorias', 'unidades-medida', 'config-sistema-datos', 'importaciones',
      'metodos-cobro', 'condiciones-pago', 'tipos-comprobante', 'presupuestos-config', 'config-facturacion', 'config-reportes', 'logs'
    ],
    highlights: ['Facturación electrónica ARCA', 'Costos y listas de precios', 'Compras y pedido sugerido'],
    cta: 'Escalar con control',
  },
  enterprise: {
    marketingName: 'Plan Dirección',
    badge: 'Estrategia completa',
    audience: 'Para producción, multiunidad o dueños que deciden con números reales.',
    summary: 'Producción, flujo de fondos y control de rentabilidad de punta a punta.',
    price_monthly: 0,
    operation_limit: 0,
    modules: [
      'ventas-pos', 'ventas-historial', 'caja', 'clientes', 'facturacion', 'productos', 'inventario', 'inventario-ajuste',
      'compras-nueva', 'pedido-sugerido', 'compras', 'proveedores', 'bancos', 'tesoreria-cobranzas', 'tesoreria-pagos', 'gastos',
      'produccion-registrar', 'pedidos-produccion', 'recetas', 'rentabilidad-ventas', 'gestion-costos', 'flujo-fondos', 'conciliacion-bancaria',
      'empresa-datos', 'permisos', 'sitios', 'categorias', 'unidades-medida', 'config-sistema-datos', 'importaciones',
      'metodos-cobro', 'condiciones-pago', 'tipos-comprobante', 'presupuestos-config', 'config-facturacion', 'config-reportes', 'logs'
    ],
    highlights: ['Producción y recetas', 'Flujo de fondos', 'Control de rentabilidad'],
    cta: 'Dirigir con margen',
  },
};

// Fallback local del trial. Tiene que coincidir con app_settings.default_trial_days
// y con la copy publica: eran tres numeros distintos (15 aca, 14 en la web, 60
// efectivos por un trigger de la base). La fuente de verdad es app_settings;
// esto es solo el valor por si la consulta falla.
export const DEFAULT_TRIAL_DAYS = 14;

// Oferta de lanzamiento (campaña Meta, nov 2026), para las altas hechas hasta
// `until` (inclusive, día local), durante los primeros `months` meses:
//   - Plan Arranque a precio fijo ($23.800 + IVA).
//   - Plan Control y Plan Dirección con 50% OFF sobre lista.
// Es la ÚNICA fuente de la copy de la oferta en el sitio: landing, precios y
// shell público leen de acá.
// OJO: el cobro todavía no aplica la promo (cobra price_monthly). Eso va
// server-side en las edge functions de cobro, en un cambio aparte.
export const LAUNCH_OFFER = {
  months: 6,
  until: '2026-11-30',
  untilLabel: '30/11',
  plusIva: true,
  plans: {
    basic: { planName: 'Plan Arranque', price: 23800 },
    premium: { planName: 'Plan Control', discountPercent: 50 },
    enterprise: { planName: 'Plan Dirección', discountPercent: 50 },
  },
  headlinePlanKey: 'basic',
};

export const LAUNCH_HEADLINE_PRICE = LAUNCH_OFFER.plans[LAUNCH_OFFER.headlinePlanKey].price;

const todayLocalKey = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const isLaunchOfferActive = (dateKey = todayLocalKey()) => dateKey <= LAUNCH_OFFER.until;

// Precio de lanzamiento de un plan (sin IVA), o null si el plan no participa,
// no hay precio de lista válido para calcular el descuento, o la oferta venció.
export const getLaunchPrice = (planKey, listPrice, dateKey) => {
  const rule = LAUNCH_OFFER.plans[planKey];
  if (!rule || !isLaunchOfferActive(dateKey)) return null;
  if (rule.price) return rule.price;
  const list = Number(listPrice) || 0;
  if (!rule.discountPercent || list <= 0) return null;
  return Math.round(list * (1 - rule.discountPercent / 100));
};

// Resumen de una línea para la copy: "Plan Arranque a $23.800 + IVA/mes y 50% OFF en
// Control y Dirección durante 6 meses". `fmt` formatea montos (formatPlanPrice).
export const getLaunchSummary = (fmt) =>
  `Plan Arranque a ${fmt(LAUNCH_HEADLINE_PRICE)} + IVA/mes y 50% OFF en Control y Dirección durante ${LAUNCH_OFFER.months} meses`;

export const getLaunchLabel = (planKey) => {
  const rule = LAUNCH_OFFER.plans[planKey];
  if (!rule) return null;
  return rule.price
    ? `Precio de lanzamiento por ${LAUNCH_OFFER.months} meses`
    : `${rule.discountPercent}% OFF por ${LAUNCH_OFFER.months} meses`;
};

export const VALID_PROMO_CODES = {
  INSTA50: {
    code: 'INSTA50',
    discountPercent: 50,
    months: 3,
    description: '50% OFF durante los primeros 3 meses de suscripción',
  },
  IG50OFF: {
    code: 'IG50OFF',
    discountPercent: 50,
    months: 3,
    description: '50% OFF durante los primeros 3 meses de suscripción',
  },
};

export const validatePromoCode = (inputCode) => {
  if (!inputCode) return null;
  const clean = String(inputCode).trim().toUpperCase();
  return VALID_PROMO_CODES[clean] || null;
};

export default BILLING_BRAND;

